import { useTheme } from "@/app/providers/theme-provider"
import { KLERI_PATH } from "@/components/ui/kleri-logo-utils"
import { useBothLogosValidation } from "@/hooks/use-logo-validation"
import { getInjectedAsset, injectedAspectRatio } from "@/lib/runtime-branding"
import { cn } from "@/lib/utils"

// Create URLs for external logo files using Vite's import.meta.url
// These URLs are resolved relative to this module and processed by Vite at build time
// We check at runtime if the files actually exist before using them
const logoLightUrl = new URL("./logo-light.svg", import.meta.url).href
const logoDarkUrl = new URL("./logo-dark.svg", import.meta.url).href

type SmallLogoProps = {
  className?: string
}

/**
 * Embedded SVG fallback - used when external logo files don't exist
 * This is the default Kleri wordmark (main branch)
 * Customer branches should replace this with their own logo
 */
/**
 * Platform fallback for the small (avatar-sized) slot: the brand avatar —
 * full Kleri wordmark centred in a blue circle with padding (never a monogram
 * or a cropped mark). Tenants still get their uploaded small logo via the
 * injected-asset path above.
 */
function EmbeddedSmallLogoSVG({ className }: SmallLogoProps) {
  return (
    <svg
      aria-hidden
      className={cn("", className)}
      viewBox="0 0 400 400"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="200" cy="200" fill="#A6E7FF" r="200" />
      <g transform="translate(60 164) scale(0.18)">
        <path d={KLERI_PATH} fill="#34393B" />
      </g>
    </svg>
  )
}

export function SmallLogo({ className }: SmallLogoProps) {
  const { theme } = useTheme()
  const logoStatus = useBothLogosValidation(logoLightUrl, logoDarkUrl)

  // Use external logos with theme switching
  let effectiveTheme: "light" | "dark"
  if (theme === "system") {
    effectiveTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  } else {
    effectiveTheme = theme
  }

  // An admin-uploaded logo wins over the build-time file — that is the point of
  // branding-as-configuration (#2741). It also skips the validation fetch: the
  // server only injects a ref for an asset it actually stored, so there is
  // nothing to probe and a branded tenant never sees the "pending" placeholder.
  const injectedLight = getInjectedAsset("logo_small_light")
  const injectedDark = getInjectedAsset("logo_small_dark")
  const injected =
    effectiveTheme === "dark" ? (injectedDark ?? injectedLight) : (injectedLight ?? injectedDark)
  if (injected) {
    return (
      <img
        alt="Logo"
        className={cn("h-auto transition-opacity duration-200", className)}
        src={injected.url}
        // Derived from the uploaded image rather than a per-customer constant.
        // This exact line differs on seven customer branches today.
        style={{ aspectRatio: injectedAspectRatio(injected), objectFit: "contain" }}
      />
    )
  }

  if (logoStatus === "pending") {
    return <div aria-hidden className={cn(className)} style={{ aspectRatio: "1 / 1" }} />
  }

  if (logoStatus === "invalid") {
    return <EmbeddedSmallLogoSVG className={className} />
  }

  const logoSrc = effectiveTheme === "dark" ? logoDarkUrl : logoLightUrl

  return (
    <img
      alt="Logo"
      className={cn("h-auto transition-opacity duration-200", className)}
      src={logoSrc}
      style={{ aspectRatio: "1 / 1", objectFit: "contain" }}
    />
  )
}
